/**
 * Settings → Job Codes
 *
 * Purpose: Per-firm job-code configuration. New jobs get a sequential code
 * (e.g. "J-0042") sent to InfoTrack as the client reference so search costs
 * reconcile to specific jobs in Xero. This page sets the firm-wide prefix.
 *
 * - Convex: settings.jobCode.queries.getJobCodeSettings,
 *           settings.jobCode.mutations.setJobCodePrefix
 */
import { createFileRoute } from "@tanstack/react-router";
import { convexQuery } from "@convex-dev/react-query";
import { useQuery } from "@tanstack/react-query";
import { useMutation } from "convex/react";
import { api } from "~/convex/_generated/api";
import type { Id } from "~/convex/_generated/dataModel";
import { useActiveOrg } from "@/utils/org-context";
import { Button, Input, Label } from "@/ui/primitives";
import { Hash } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

const GROUP_CARD_CLASS =
  "rounded-xl bg-background shadow-[0_1px_3px_rgba(0,0,0,0.08),0_1px_2px_rgba(0,0,0,0.06)] hover:shadow-[0_4px_12px_rgba(0,0,0,0.1),0_2px_4px_rgba(0,0,0,0.06)] transition-shadow duration-200 overflow-hidden";

export const Route = createFileRoute(
  "/_app/_auth/dashboard/_layout/settings/jobcode",
)({
  component: JobCodeSettings,
  beforeLoad: () => ({
    title: "Job Codes",
    headerTitle: "Job Codes",
    headerDescription:
      "Set the code applied to new jobs and sent to InfoTrack for cost reconciliation.",
  }),
});

function JobCodeSettings() {
  const { orgId, activeOrg } = useActiveOrg();
  const canEdit = activeOrg?.role === "owner" || activeOrg?.role === "admin";

  if (!orgId) {
    return (
      <div className="text-sm text-muted-foreground">
        Select an organisation to manage job codes.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <JobCodeCard orgId={orgId as Id<"organizations">} canEdit={canEdit} />
    </div>
  );
}

function JobCodeCard({
  orgId,
  canEdit,
}: {
  orgId: Id<"organizations">;
  canEdit: boolean;
}) {
  const { data: settings } = useQuery(
    convexQuery(api.settings.jobCode.queries.getJobCodeSettings, { orgId }),
  );
  const setJobCodePrefix = useMutation(
    api.settings.jobCode.mutations.setJobCodePrefix,
  );
  const [prefix, setPrefix] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (settings?.prefix !== undefined) setPrefix(settings.prefix);
  }, [settings?.prefix]);

  const nextCode = `${prefix.trim() || "J-"}${String(
    settings?.nextNumber ?? 1,
  ).padStart(4, "0")}`;

  const handleSave = async () => {
    setSaving(true);
    try {
      await setJobCodePrefix({ orgId, prefix: prefix.trim() });
      toast.success("Job code prefix saved.");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to save prefix.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={GROUP_CARD_CLASS}>
      <div className="border-b p-5">
        <div className="flex items-start gap-3">
          <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-muted">
            <Hash className="size-4.5 text-foreground" />
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-medium">Job Code</h3>
            <p className="mt-0.5 text-xs text-muted-foreground">
              New jobs get a sequential code, sent to InfoTrack as the client
              reference for cost reconciliation. You can override an individual
              code when creating a job or in its settings. Changing the prefix
              only affects future jobs.
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-4 p-5">
        <div className="space-y-1.5">
          <Label htmlFor="job-code-prefix">Prefix</Label>
          <Input
            id="job-code-prefix"
            value={prefix}
            onChange={(e) => setPrefix(e.target.value)}
            placeholder="J-"
            disabled={!canEdit || saving}
            maxLength={20}
          />
          <p className="text-xs text-muted-foreground">
            Next job code:{" "}
            <code className="rounded bg-muted px-1.5 py-0.5 font-mono">
              {nextCode}
            </code>
          </p>
        </div>

        {canEdit ? (
          <Button onClick={handleSave} disabled={saving}>
            Save
          </Button>
        ) : (
          <p className="text-sm text-muted-foreground">
            Only organisation owners and admins can change the job code prefix.
          </p>
        )}
      </div>
    </div>
  );
}
